import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import {
  postApiSettingsAddAppointmentTypeSetting,
  postApiSettingsAddElectronicPayerEntryConfig,
  putApiSettingsUpdateElectronicPayerEntryConfig,
  postApiSettingsUpdateAppointmentTypeSetting,
  getApiVisitNoteGetUserFavoriteMedicalCodes,
  postApiFormUpdateForm,
  postApiTemplateAddField,
  deleteApiTemplateDeleteField,
  putApiTemplateEditField,
  getApiTemplateGetFieldFieldId,
  getApiTemplateGetFieldList,
  putApiSettingsUpdatePaperPayerEntry,
  postApiSettingsAddPaperPayerEntry,
  deleteApiSettingsDeletePaperPayerEntry,
  deleteApiSettingsDeleteElectronicPayerEntryConfig,
  postApiUserCreateUser,
  postApiUserUpdateUser,
  getApiSettingsGetUserManagementSettings,
  postApiTagCreateTag,
  deleteApiTagDeleteTag,
  postApiTagUpdateTag,
  getApiTagGetTags,
  postApiSettingsAddRoomSetting,
  patchApiSettingsUpdateRoomSetting,
  deleteApiSettingsDeleteRoomSetting,
  getApiSettingsGetRoomSettings,
  getApiSettingsGetFixedAppointmentStatuses,
  postApiOrgConfigurationAddServiceFacility,
  postApiOrgConfigurationUpdateServiceFacility,
  deleteApiOrgConfigurationDeleteServiceFacility,
  postApiOrgConfigurationUpdateRenderingProvider,
  deleteApiOrgConfigurationDeleteBillingProviderRule,
  deleteApiOrgConfigurationDeleteRenderingProvider,
  deleteApiOrgConfigurationDeleteBillingProvider,
  postApiOrgConfigurationAddIndividualBillingProvider,
  postApiOrgConfigurationAddOrganizationBillingProvider,
  postApiOrgConfigurationUpdateIndividualBillingProvider,
  postApiOrgConfigurationUpdateOrganizationBillingProvider,
  postApiOrgConfigurationAddBillingProviderRule,
  postApiOrgConfigurationUpdateBillingProviderRule,
  postApiOrgConfigurationReorderBillingProviderRules,
  getApiOrgConfigurationGetBillingProviderRules,
  getApiSettingsGetAppointmentSettings,
  getApiSettingsGetPatientAppointmentChangeSettings,
  putApiSettingsUpdatePatientAppointmentChangeSettings,
} from 'endpoints';
import {
  AppointmentTypeSettingDTO,
  DeleteRequestDTO,
  FormViewDTO,
  PayerEntryConfigDTO,
  UserFavoritedMedicalCodesResponseDTO,
  TemplateFieldDTO,
  None,
  GetApiTemplateGetFieldListParams,
  TemplateFieldDTOPaginatedResponseDTO,
  PaperPayerEntryDTO,
  UpdatePaperPayerRequestDTO,
  UserDTO,
  UserManagementSettingsDTO,
  CreateOrModifyTagDTO,
  TagDTOPaginatedResponseDTO,
  GetApiTagGetTagsParams,
  RoomSettingDTO,
  ServiceFacilityDTO,
  RenderingProviderDTO,
  BillingProviderRuleDTO,
  Int64ListDTO,
  SettingsDTO,
  PatientAppointmentChangeSettingsDTO,
  DeleteApiSettingsDeleteRoomSettingParams,
  UpdateFormRequestDTO,
} from 'dtos';
import {
  isNilOrZero,
} from '@populateui/utils';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  useEffect,
} from 'react';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getAppointmentTypes,
  getBillingProviders,
  getRenderingProviders,
} from 'views/EMR/Settings/transport';
import {
  BillingProviderInfo,
  BillingConfigType,
  DeleteBillingConfigParams,
} from 'views/EMR/Settings/BillingConfigurations/types';
import {
  AppointmentTypes,
} from 'views/EMR/Settings/Appointments/types';
import {
  UserRequest,
} from 'views/EMR/Settings/Users/Dialog';
import {
  TagDTO,
} from 'dtos/tagDTO';
import {
  serializeBillingProviderRules,
  serializeServiceFacility,
} from 'views/EMR/Settings/helper';
import {
  BillingProviderRuleForm,
} from 'views/EMR/Settings/BillingConfigurations/BillingProviderRules/Dialog';
import {
  useAuth,
} from 'core/hooks';
import {
  useImpersonation,
} from 'core/context/impersonation';

export function useUpdateForm():
  UseMutationResult<
    FormViewDTO,
    Error,
    UpdateFormRequestDTO
    > {
  return useMutation<FormViewDTO, Error, UpdateFormRequestDTO>({
    mutationFn: (data) => postApiFormUpdateForm(data),
  });
}

export function useUpsertAppointmentType(isNew?: boolean):
  UseMutationResult<AppointmentTypeSettingDTO, Error, AppointmentTypeSettingDTO> {
  return useMutation<AppointmentTypeSettingDTO, Error, AppointmentTypeSettingDTO>({
    mutationFn: (data: AppointmentTypeSettingDTO) => (
      isNew
        ? postApiSettingsAddAppointmentTypeSetting(data)
        : postApiSettingsUpdateAppointmentTypeSetting(data)),
  });
}

export function useGetAppointmentTypes(): AppointmentTypes {
  const {
    isAuthenticated,
    isGoogleOAuth2,
  } = useAuth();

  const { isImpersonating } = useImpersonation();

  const isPortal = isAuthenticated && isGoogleOAuth2 && !isImpersonating;

  const {
    data: appointmentSettings,
    refetch: refetchAppointmentTypeSettings,
    isLoading,
  } = usePrefetchedData<AppointmentTypes>({
    key: ['appointment-type-settings'],
    fetchFn: getAppointmentTypes,
    enabled: !isPortal,
  });

  useEffect(() => {
    const unsubscribe = settingsEventChannel.on('onAppointmentTypesChange', () => {
      refetchAppointmentTypeSettings?.();
    });
    return () => { unsubscribe(); };
  }, []);

  const result = appointmentSettings ?? {
    appointmentTypes: [],
    durationByType: {},
    isTelehealthByType: {},
  };

  return {
    ...result,
    isLoading,
  };
}

export function useGetSettingsGetAppointmentSettings() {
  return usePrefetchedData<SettingsDTO>({
    key: ['appointment-settings'],
    fetchFn: getApiSettingsGetAppointmentSettings,
  });
}

export function useGetPatientAppointmentChangeSettings():
UseQueryResult<PatientAppointmentChangeSettingsDTO, Error> {
  return useQuery<PatientAppointmentChangeSettingsDTO, Error>({
    queryKey: ['patient-appointment-change-settings'],
    queryFn: getApiSettingsGetPatientAppointmentChangeSettings,
  });
}

export function useUpdatePatientAppointmentChangeSettings():
UseMutationResult<PatientAppointmentChangeSettingsDTO, Error, PatientAppointmentChangeSettingsDTO> {
  return useMutation<PatientAppointmentChangeSettingsDTO, Error, PatientAppointmentChangeSettingsDTO>({
    mutationFn: (payload) => putApiSettingsUpdatePatientAppointmentChangeSettings(payload),
  });
}

export function useUpsertElectronicPayerConfig():
  UseMutationResult<PayerEntryConfigDTO, Error, PayerEntryConfigDTO> {
  return useMutation<PayerEntryConfigDTO, Error, PayerEntryConfigDTO>({
    mutationFn: (payload) => (
      isNil(payload?.payerEntryConfigId)
        ? postApiSettingsAddElectronicPayerEntryConfig(payload)
        : putApiSettingsUpdateElectronicPayerEntryConfig(payload)
    ),
  });
}

export function useUpsertPaperPayerConfig():
  UseMutationResult<PaperPayerEntryDTO, Error, UpdatePaperPayerRequestDTO> {
  return useMutation<PaperPayerEntryDTO, Error, UpdatePaperPayerRequestDTO>({
    mutationFn: (payload) => (
      isNil(payload?.payerEntryId)
        ? postApiSettingsAddPaperPayerEntry(payload)
        : putApiSettingsUpdatePaperPayerEntry(payload)
    ),
  });
}

export function useDeletePayerConfig():
  UseMutationResult< void, Error, PayerEntryConfigDTO > {
  return useMutation< void, Error, PayerEntryConfigDTO >({
    mutationFn: async (data) => {
      if (data?.payerEntry?.isPaper) {
        await deleteApiSettingsDeletePaperPayerEntry({
          payerEntryId: data?.payerEntryId ?? 0,
        });
      } else {
        await deleteApiSettingsDeleteElectronicPayerEntryConfig({
          payerEntryConfigId: data?.payerEntryConfigId ?? 0,
        });
      }
    },
  });
}

export function useGetFavoriteCodes(): UseQueryResult<UserFavoritedMedicalCodesResponseDTO, Error> {
  return useQuery<UserFavoritedMedicalCodesResponseDTO, Error>({
    queryKey: ['template-favorite'],
    queryFn: getApiVisitNoteGetUserFavoriteMedicalCodes,
  });
}

export function useGetFields(
  params: GetApiTemplateGetFieldListParams,
): UseQueryResult<TemplateFieldDTOPaginatedResponseDTO, Error> {
  return useQuery<TemplateFieldDTOPaginatedResponseDTO, Error>({
    queryKey: ['fields', JSON.stringify(params)],
    queryFn: () => getApiTemplateGetFieldList(params),
  });
}

export function useGetFieldById(id: number = 0): UseQueryResult<TemplateFieldDTO, Error> {
  return useQuery<TemplateFieldDTO, Error>({
    queryKey: ['field', id],
    queryFn: () => getApiTemplateGetFieldFieldId(id),
    enabled: !isNilOrZero(id),
  });
}

export function useUpsertField(): UseMutationResult<TemplateFieldDTO, Error, TemplateFieldDTO> {
  return useMutation<TemplateFieldDTO, Error, TemplateFieldDTO>({
    mutationFn: async (data) => {
      const hasId = !isNilOrZero(data?.templateFieldId);
      const requestFn = hasId ? putApiTemplateEditField : postApiTemplateAddField;
      const response = await requestFn(data);
      return response as TemplateFieldDTO;
    },
  });
}

export function useDeleteField(): UseMutationResult<None, Error, TemplateFieldDTO> {
  return useMutation<None, Error, TemplateFieldDTO>({
    mutationFn: (data) => deleteApiTemplateDeleteField(data),
  });
}

export function useGetUserManagementSettings(): UseQueryResult<UserManagementSettingsDTO, Error> {
  return usePrefetchedData<UserManagementSettingsDTO>({
    key: ['user-settings'],
    fetchFn: getApiSettingsGetUserManagementSettings,
  });
}

export function useUpsertUser(isNew?: boolean):
  UseMutationResult<UserDTO, Error, UserRequest> {
  return useMutation<UserDTO, Error, UserRequest>({
    mutationFn: (data) => (isNew
      ? postApiUserCreateUser(data)
      : postApiUserUpdateUser(data)
    ),
  });
}

export function useSearchTags(
  search: string,
  page: number,
  pageSize: number,
  enabled = true,
): UseQueryResult<TagDTOPaginatedResponseDTO, Error> {
  return useQuery<TagDTOPaginatedResponseDTO, Error>({
    queryKey: ['tags', search],
    queryFn: async () => getApiTagGetTags({
      search,
      page,
      pageSize,
    } as GetApiTagGetTagsParams),
    enabled,
  });
}

export function useUpsertTag():
  UseMutationResult<TagDTO, Error, CreateOrModifyTagDTO> {
  return useMutation<TagDTO, Error, CreateOrModifyTagDTO>({
    mutationFn: (data) => (
      isNil(data?.tagId)
        ? postApiTagCreateTag(data)
        : postApiTagUpdateTag(data)
    ),
  });
}

export function useUpsertServiceFacility():
  UseMutationResult<ServiceFacilityDTO, Error, ServiceFacilityDTO> {
  return useMutation<ServiceFacilityDTO, Error, ServiceFacilityDTO>({
    mutationFn: (data: ServiceFacilityDTO) => {
      const serializedData = serializeServiceFacility(data);
      return (
        isNil(data?.serviceFacilityId)
          ? postApiOrgConfigurationAddServiceFacility(serializedData)
          : postApiOrgConfigurationUpdateServiceFacility(serializedData)
      );
    },
  });
}

// Rendering Provider
export function useGetRenderingProvider(): UseQueryResult< RenderingProviderDTO[], Error> {
  return useQuery<RenderingProviderDTO[], Error>({
    queryKey: ['rendering-providers'],
    queryFn: getRenderingProviders,
  });
}

export function useUpdateRenderingProvider():
  UseMutationResult<RenderingProviderDTO, Error, RenderingProviderDTO> {
  return useMutation<RenderingProviderDTO, Error, RenderingProviderDTO>({
    mutationFn: (data) => postApiOrgConfigurationUpdateRenderingProvider(data),
  });
}

// Billing Provider
export function useGetBillingProvider(): UseQueryResult< RenderingProviderDTO[], Error> {
  return useQuery<RenderingProviderDTO[], Error>({
    queryKey: ['billing-providers'],
    queryFn: getBillingProviders,
  });
}

export function useUpsertBillingProvider():
  UseMutationResult<ServiceFacilityDTO, Error, BillingProviderInfo> {
  return useMutation<ServiceFacilityDTO, Error, BillingProviderInfo>({
    mutationFn: (data: BillingProviderInfo) => {
      const isIndividual = data?.type === 'Individual';
      const add = isIndividual
        ? postApiOrgConfigurationAddIndividualBillingProvider
        : postApiOrgConfigurationAddOrganizationBillingProvider;

      const update = isIndividual
        ? postApiOrgConfigurationUpdateIndividualBillingProvider
        : postApiOrgConfigurationUpdateOrganizationBillingProvider;

      return isNil(data?.billingProviderId) ? add(data) : update(data);
    },
  });
}

// Billing Provider Rule
export function useGetBillingProviderRules(
  includeSummaries: boolean = true,
): UseQueryResult<BillingProviderRuleDTO[], Error> {
  return useQuery<BillingProviderRuleDTO[], Error>({
    queryKey: ['billingProvider-rules'],
    queryFn: async () => {
      const response = await getApiOrgConfigurationGetBillingProviderRules({ includeSummaries });
      return response?.data ?? [];
    },
  });
}

export function useReorderBillingProviderRule():
  UseMutationResult<BillingProviderRuleDTO[], Error, Int64ListDTO > {
  return useMutation<BillingProviderRuleDTO[], Error, Int64ListDTO >({
    mutationFn: (rules) => postApiOrgConfigurationReorderBillingProviderRules(rules),
  });
}

export function useUpdateBillingProviderRule():
UseMutationResult<BillingProviderRuleDTO, Error, BillingProviderRuleForm> {
  return useMutation<BillingProviderRuleDTO, Error, BillingProviderRuleForm>({
    mutationFn: (data: BillingProviderRuleForm) => {
      const serializedData = serializeBillingProviderRules(data);

      return (
        isNil(serializedData?.billingProviderRuleId)
          ? postApiOrgConfigurationAddBillingProviderRule(serializedData)
          : postApiOrgConfigurationUpdateBillingProviderRule(serializedData)
      );
    },
  });
}

// common
const configTransportFunctionsByType = Object.freeze({
  serviceFacility: {
    delete: deleteApiOrgConfigurationDeleteServiceFacility,
  },
  renderingProvider: {
    delete: deleteApiOrgConfigurationDeleteRenderingProvider,
  },
  billingProvider: {
    delete: deleteApiOrgConfigurationDeleteBillingProvider,
  },
  billingProviderRule: {
    delete: deleteApiOrgConfigurationDeleteBillingProviderRule,
  },
});

export function useDeleteBillingConfig(
  configType: BillingConfigType,
): UseMutationResult< void, Error, DeleteBillingConfigParams > {
  const transportFunc = configTransportFunctionsByType[configType];
  return useMutation<void,
    Error, DeleteBillingConfigParams>({
      mutationFn: (params) => transportFunc?.delete(params),
    });
}

export function useUpsertRoom():
  UseMutationResult<None, Error, RoomSettingDTO> {
  return useMutation<None, Error, RoomSettingDTO>({
    mutationFn: (payload) => (
      isNil(payload?.roomSettingId)
        ? postApiSettingsAddRoomSetting(payload)
        : patchApiSettingsUpdateRoomSetting(payload)
    ),
  });
}

export function useDeleteTag():
  UseMutationResult<TagDTO, Error, DeleteRequestDTO> {
  return useMutation<TagDTO, Error, DeleteRequestDTO>({
    mutationFn: (data) => deleteApiTagDeleteTag(data),
  });
}

export function useDeleteRoom(): UseMutationResult<
  None,
  Error,
  DeleteApiSettingsDeleteRoomSettingParams
  > {
  return useMutation<None, Error, DeleteApiSettingsDeleteRoomSettingParams>({
    mutationFn: (params) => deleteApiSettingsDeleteRoomSetting(params),
  });
}

export function useGetRoomSettings(): UseQueryResult<RoomSettingDTO[], Error> {
  return useQuery<RoomSettingDTO[], Error>({
    queryKey: ['room-settings'],
    queryFn: getApiSettingsGetRoomSettings,
  });
}

export function useGetAppointmentStatuses(): UseQueryResult<string[], Error> {
  return useQuery<string[], Error>({
    queryKey: ['appointment-statuses-only'],
    queryFn: getApiSettingsGetFixedAppointmentStatuses,
  });
}
